import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import SEO, { type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import { staticPage } from '@/lib/content';
import { AUTHOR, BOOKING_URL, EMAIL, LEAD_ENGINE_URL, LEAD_IDEMPOTENCY_ENABLED, PHONE, PHONE_E164 } from '@/lib/site';

import { leadAttempt, retryDelayMs, type LeadAttempt } from '@/lib/lead-retry';
import { CONTACT_TOPICS, contactTopic, type ContactTopic } from '@/lib/contact-topic';

type Status = 'idle' | 'submitting' | 'success' | 'error';
const REQUEST_TIMEOUT_MS = 15000;

const Contact = () => {
  const page = staticPage('/contact');
  const { search, pathname } = useLocation();
  const { topicKey, topic: initialTopic, scoping: requestedScoping } = contactTopic(search);

  // Query variants share the same prerendered /contact HTML. Match that
  // neutral markup during hydration, then apply the requested context.
  const [topic, setTopic] = useState<ContactTopic>('Something else');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [ready, setReady] = useState(false);
  const scoping = ready && requestedScoping;
  const request = useRef<AbortController | null>(null);
  const attempt = useRef<LeadAttempt | null>(null);
  const [retryAt, setRetryAt] = useState(0);
  useEffect(() => {
    if (!retryAt) return;
    const timer = window.setTimeout(() => setRetryAt(0), Math.max(0, retryAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [retryAt]);

  // The lead engine accepts JSON, not a native browser form submission.
  // Keep the prerendered form disabled until its submit handler is available.
  useEffect(() => {
    setReady(true);
    return () => {
      request.current?.abort();
      request.current = null;
    };
  }, []);

  useEffect(() => {
    setTopic(initialTopic);
  }, [initialTopic, search]);

  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Contact', href: '/contact' },
  ];

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ready || request.current || retryAt > Date.now()) return;
    const form = e.currentTarget;
    // Native required fields must also reject whitespace-only values.
    for (const name of ['name', 'email', 'message']) {
      const field = form.elements.namedItem(name);
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
        field.value = field.value.trim();
      }
    }
    if (!form.reportValidity()) return;
    const fd = new FormData(form);
    const payload = {
      brand: 'ecmworks',
      name: String(fd.get('name') ?? '').trim(),
      email: String(fd.get('email') ?? '').trim(),
      company: String(fd.get('company') ?? '').trim(),
      phone: String(fd.get('phone') ?? '').trim(),
      topic: String(fd.get('topic') ?? ''),
      message: String(fd.get('message') ?? '').trim(),
      honeypot: String(fd.get('website') ?? ''),
      // Keep supported inquiry context, without copying arbitrary query data.
      source_path: `${pathname}${topicKey ? `?topic=${encodeURIComponent(topicKey)}` : ''}`,
    };
    const body = JSON.stringify(payload);
    if (LEAD_IDEMPOTENCY_ENABLED) attempt.current = leadAttempt(attempt.current, body, () => crypto.randomUUID());
    const controller = new AbortController();
    request.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    setErrorMessage('');
    setStatus('submitting');
    let serviceMessage = '';
    try {
      const res = await fetch(LEAD_ENGINE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(LEAD_IDEMPOTENCY_ENABLED && attempt.current ? { 'Idempotency-Key': attempt.current.key } : {}) },
        body,
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
      if (!res.ok) {
        if (request.current !== controller) return;
        const delay = [429, 503].includes(res.status) ? retryDelayMs(res.headers.get('Retry-After'), Date.now()) : 0;
        if (delay) setRetryAt(Date.now() + delay);
        if (res.status === 429) serviceMessage = 'The inquiry service has reached its submission limit. Please wait before trying again, or email us.';
        if (res.status === 503 && delay) serviceMessage = 'The inquiry service is temporarily busy. Please wait before retrying the same message.';
        if (res.status === 409) serviceMessage = 'The inquiry service could not safely reconcile this retry. Please email us so we can check whether your earlier message arrived.';
        throw new Error(`HTTP ${res.status}`);
      }
      const receipt: unknown = await res.json();
      if (!receipt || typeof receipt !== 'object' || !('ok' in receipt) || receipt.ok !== true ||
          !('id' in receipt) || typeof receipt.id !== 'string' || !receipt.id.trim()) {
        throw new Error('The inquiry service did not confirm a saved message');
      }
      if (request.current !== controller) return;
      attempt.current = null;
      setRetryAt(0);
      setStatus('success');
      form.reset();
    } catch {
      if (request.current !== controller) return;
      setErrorMessage(serviceMessage || (controller.signal.aborted
        ? 'The request took too long. We could not confirm whether your message was received.'
        : 'We could not confirm whether your message was received.'));
      setStatus('error');
    } finally {
      window.clearTimeout(timeout);
      if (request.current === controller) request.current = null;
    }
  };

  return (
    <>
      <SEO title={page.title} description={page.description} path="/contact" breadcrumbs={crumbs} />
      <PageHeader
        crumbs={crumbs}
        label="Contact"
        title={scoping ? 'Book a scoping call.' : 'Talk to us.'}
        lead={
          scoping
            ? 'A short, structured call to size the work. Tell us what is in the estate and what you are trying to do; we come back with scope, sequence and the first steps.'
            : 'Tell us what is changing or getting in the way. Share the versions and integrations you know; a complete inventory can come later. We normally reply within one working day.'
        }
      />

      <section className="container py-14 md:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16">
          <div className="max-w-[40rem]">
            {status === 'success' ? (
              <div role="status" className="border border-forest bg-cream-light p-8">
                <p className="label">Submitted</p>
                <h2 className="mt-3 text-subhead">Thank you for your inquiry.</h2>
                <p className="mt-3 text-body">
                  We normally reply within one working day. You can also reach us at{' '}
                  <a href={`mailto:${EMAIL}`} className="link">{EMAIL}</a>.
                </p>
              </div>
            ) : (
              <form action="/contact" method="post" onSubmit={onSubmit} aria-busy={status === 'submitting'}>
                <p className="mb-6 text-small">
                  Prefer email? Write to <a href={`mailto:${EMAIL}`} className="link">{EMAIL}</a>.
                  {' '}This is also an alternative if the online form is unavailable.
                </p>
                <noscript>
                  <p className="mb-6 border border-forest bg-cream-light p-5 text-small">
                    To send an inquiry, email{' '}
                    <a href={`mailto:${EMAIL}`} className="link">{EMAIL}</a>{' '}
                    or book a call using the link alongside this form.
                  </p>
                </noscript>
                <fieldset disabled={!ready || status === 'submitting'} className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="field-label">
                        Name
                      </label>
                      <input id="name" name="name" type="text" required maxLength={100} autoComplete="name" className="field" />
                    </div>
                    <div>
                      <label htmlFor="email" className="field-label">
                        Work email
                      </label>
                      <input id="email" name="email" type="email" required maxLength={254} autoComplete="email" className="field" />
                    </div>
                    <div>
                      <label htmlFor="company" className="field-label">
                        Organization <span className="font-normal">(optional)</span>
                      </label>
                      <input id="company" name="company" type="text" maxLength={200} autoComplete="organization" className="field" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="field-label">
                        Phone <span className="font-normal">(optional)</span>
                      </label>
                      <input id="phone" name="phone" type="tel" maxLength={30} autoComplete="tel" className="field" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="topic" className="field-label">
                      Topic
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      className="field"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value as ContactTopic)}
                    >
                      {CONTACT_TOPICS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="field-label">
                      The estate, in a few lines
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={7}
                      required
                      maxLength={3000}
                      aria-describedby="message-help"
                      className="field"
                      placeholder="Versions, components, integrations, and what you are trying to do."
                    />
                    <p id="message-help" className="mt-3 text-small">
                      Include versions, integrations, the change you are planning and your deadline.
                      Leave passwords, customer documents and sensitive configuration out of this form.
                      We can agree a secure way to review detailed material if needed.
                    </p>
                  </div>

                  {/* The lead service receives this honeypot for its server-side spam checks. */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                  </div>

                  {status === 'error' && (
                    <p role="alert" className="border border-forest bg-cream-light px-5 py-4 text-small">
                      {errorMessage} Your details are still here. You can email{' '}
                      <a href={`mailto:${EMAIL}`} className="link">{EMAIL}</a>.
                    </p>
                  )}

                  <p className="text-small">
                    We use your inquiry details to respond to you. Read how the contact service and this site
                    handle information in our <a href="/privacy" className="link">privacy notice</a>.
                  </p>
                  <button type="submit" className="btn-forest" disabled={!ready || status === 'submitting' || retryAt > 0}>
                    {status === 'submitting' ? 'Sending…' : retryAt > 0 ? 'Please wait before retrying' : scoping ? 'Request a scoping call' : 'Send message'}
                  </button>
                </fieldset>
              </form>
            )}
          </div>

          <aside className="lg:pt-1">
            <div className="border border-forest bg-cream-light p-7">
              <div className="flex items-center gap-4">
                <img
                  src="/images/andrew-blackman-small.webp"
                  srcSet="/images/andrew-blackman-small.webp 320w, /images/andrew-blackman.webp 640w"
                  sizes="80px"
                  width="320"
                  height="320"
                  loading="lazy"
                  decoding="async"
                  alt="Andrew Blackman"
                  className="block h-20 w-20 shrink-0 border border-rule"
                />
                <div className="min-w-0">
                  <p className="label">Founder</p>
                  <h2 className="mt-2 text-lg font-semibold leading-snug">{AUTHOR.name}</h2>
                </div>
              </div>
              <div className="mt-6 border-t border-rule pt-6">
                <p className="label">Direct</p>
                <p className="mt-2 text-small">
                  <a href={`mailto:${EMAIL}`} className="link">{EMAIL}</a>
                </p>
                {PHONE_E164 && (
                  <p className="mt-2 text-small">
                    <a href={`tel:${PHONE_E164}`} className="link">{PHONE}</a>
                  </p>
                )}
                <p className="mt-2 text-small">
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-[0.3em]">
                    Book a 30-minute call
                  </a>
                </p>
                <p className="mt-6 text-body">
                  Written replies within one working day.
                </p>
              </div>
            </div>
            <div className="mt-6 border-t border-rule pt-6">
              <p className="label">What to include</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-body">
                <li>Component versions (Content, Imaging, Capture, WebLogic)</li>
                <li>The ERP it is tied to, and its version</li>
                <li>Customizations and custom components you know of</li>
                <li>What you are trying to do, and by when</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Contact;
